import PropTypes from 'prop-types';
import React, { memo, useMemo } from 'react';
import { Map } from 'immutable';
import { Link } from 'react-router-dom';
import { ContextMenu, MenuItem, ContextMenuTrigger } from 'react-contextmenu';
import classNames from 'classnames';

import { InputProps } from '../../../helpers/AutoComplete';
import UrlMaker from '../../../../routing/UrlMaker';
import Span from '../../../helpers/Span';
import { unwrap, isChanged, toJS } from '../../../../utils/ChangeSpy';
import uniqueId from '../../../../utils/uniqueId';
import Input from '../../../helpers/Input';
import Textarea from '../../../helpers/Textarea';
import FormGroup from '../../../helpers/FormGroup';
import { IconLink } from '../../../helpers/IconLink';
import { IconButton } from '../../../helpers/IconButton';
import { Collapse } from '../../../helpers/Collapse';

import style from './style.module.css';

const ContextMenuWrapper = (onClick, isFirst) => {
  if (isFirst) return value => <div>{value}</div>;

  return (value, data) => {
    const id = uniqueId();

    return (
      <div>
        <ContextMenuTrigger id={id}>{value}</ContextMenuTrigger>

        <ContextMenu id={id}>
          <MenuItem data={data.toJS()} onClick={onClick}>
            <i className="fa fa-remove" /> Delete
          </MenuItem>
        </ContextMenu>
      </div>
    );
  };
};

const ApprovedStatus = ({ approved }) => {
  const isApproved = unwrap(approved);

  const iconClassName = classNames('fa', {
    'fa-check-circle-o text-success': isApproved === true,
    'fa-times text-danger': isApproved === false,
  });

  return (
    <Span checkOn="data-value" data-value={approved}>
      <i className={iconClassName} />
    </Span>
  );
};

const ApprovedSwitcher = ({ approved, changeApprove }) => {
  const isApproved = unwrap(approved);

  return (
    <FormGroup>
      <Span checkOn="data-value" className={style.approvedButtons} data-value={approved}>
        {isApproved !== false && (
          <IconButton
            active={isApproved === true}
            icon="check-circle-o"
            onClick={event => changeApprove(event, { target: 'on' })}
            title="Approved"
            variant="success"
          />
        )}
        {isApproved !== true && (
          <IconButton
            active={isApproved === false}
            icon="times"
            onClick={event => changeApprove(event, { target: 'off' })}
            title="Not approved"
            variant="danger"
          />
        )}
      </Span>
    </FormGroup>
  );
};

/**
 * Show red triangle icon on header bar, if buyer information change.
 */
function getHeaderPanelClass(buyer, isOpened) {
  const buyerChanged =
    isChanged(buyer.getIn(['activeBuyer'])) ||
    isChanged(buyer.getIn(['priority'])) ||
    isChanged(buyer.getIn(['bsnHarvco'])) ||
    isChanged(buyer.getIn(['approved'])) ||
    isChanged(buyer.getIn(['dsplProjectCategory'])) ||
    isChanged(buyer.getIn(['bsnClient'])) ||
    isChanged(buyer.getIn(['harveyComment']));

  return classNames('panel-heading cursor-grab', {
    changed: buyerChanged && !isOpened,
  });
}

/**
 * Company buyer component.
 *
 * @param props {Object}.
 * @param props.buyer {Immutable.Map} Buyer definition.
 * @returns {XML}
 * @class
 */
const CompanyBuyerComponent = ({
  id = uniqueId(),
  isFirst,
  prefix,
  onDelete,
  onClick,
  buyer,
  onActivate,
  onChangeApprove,
  suggestApprovalList,
  suggestProject,
  statuses,
  onUpdateHighStatus,
  canEditData,
}) => {
  const isOpened = buyer.get('isOpened');
  const currentStatus = buyer.getIn(['currentStatus']);
  const highStatus = buyer.getIn(['highStatus']);
  const harveyComment = toJS(buyer.get('harveyComment'));
  const clientComment = toJS(buyer.get('bsnClient'));

  const checkDefaultComment = commentType => {
    let harveyCommentDefault = false;
    let clientCommentDefault = false;

    if (!harveyComment && !clientComment) {
      harveyCommentDefault = true;
      clientCommentDefault = false;
    } else if (harveyComment && clientComment) {
      harveyCommentDefault = false;
      clientCommentDefault = true;
    } else {
      harveyCommentDefault = !!harveyComment;
      clientCommentDefault = !!clientComment;
    }

    switch (commentType) {
      case 'client':
        return clientCommentDefault;

      case 'harvey':
        return harveyCommentDefault;

      default:
        return false;
    }
  };

  const currentStatusValue = unwrap(currentStatus);
  const highStatusValue = unwrap(highStatus);
  const currentTitle = statuses.size
    ? statuses.find(status => status.get('value') === currentStatusValue, null, statuses.get(0)).get('name')
    : '';

  const highTitle = statuses.size
    ? statuses.find(status => status.get('value') === highStatusValue, null, statuses.get(0)).get('name')
    : '';

  let headerContent = unwrap(buyer.get('dsplBuyerAbbrName')) || unwrap(buyer.get('dsplBuyerLegalName'));

  if (isOpened) {
    const companyId = buyer.get('buyerId');
    const url = UrlMaker.create('/company/:companyId/buyer').mapParams({
      companyId,
    });

    headerContent = (
      <Link target="_blank" to={url}>
        {headerContent}
      </Link>
    );
  }

  const activeState = unwrap(buyer.getIn(['activeBuyer'], false)) ? (
    'currently active'
  ) : (
    <a onClick={onActivate}>make active</a>
  );
  const approved = buyer.getIn(['approved']);
  const stateClass = classNames({
    changed: isChanged(buyer.getIn(['activeBuyer'])),
  });
  const headerPanelClass = getHeaderPanelClass(buyer, isOpened);

  const callUpdateHighStatus = (event, buyer) => {
    event.stopPropagation();
    event.preventDefault();

    if (canEditData) {
      onUpdateHighStatus(buyer);
    }
  };
  const hasUpdated = !!buyer.getIn(['highStatusUpdated']);
  const statusClassName = classNames({
    'cursor-pointer': hasUpdated,
    'cursor-grab': !hasUpdated,
  });

  const header = ContextMenuWrapper(onDelete, isFirst)(
    <div className={headerPanelClass} onClick={onClick}>
      <h4 className="panel-title cursor-pointer"> {headerContent} </h4>
      <span className="pull-right">
        <b>
          <Span
            checkOn="children"
            className={statusClassName}
            onClick={event => {
              callUpdateHighStatus(event, buyer);
            }}
            title={currentTitle}
          >
            {currentStatus}
          </Span>
        </b>
        <span className={statusClassName}> / </span>
        <Span
          checkOn="children"
          className={statusClassName}
          onClick={event => {
            callUpdateHighStatus(event, buyer);
          }}
          title={highTitle}
        >
          {highStatus}
        </Span>
        <span />
        <Span checkOn="children">{buyer.getIn(['priority'])}</Span>
        <span />
        <ApprovedStatus approved={approved} />
      </span>
    </div>,
    buyer,
  );

  const projectId = useMemo(() => unwrap(buyer.get('projectId')), [buyer]);
  const approvalListId = useMemo(() => unwrap(buyer.get('approvalListId')), [buyer]);

  const approvalListLink = useMemo(
    () => (approvalListId ? <IconLink href={`/project/${projectId}/approval/${approvalListId}`} /> : null),
    [projectId, approvalListId],
  );

  const projectLink = useMemo(() => (projectId ? <IconLink href={`/project/${projectId}`} /> : null), [projectId]);

  const body = (
    <Collapse className={classNames('panel-collapse', { in: isOpened })} id={id} isOpen={isOpened} unmountOnExit>
      <div className="panel-body">
        <div className="highlight">
          <div className="row">
            <div className="col-xs-6">
              <div className="form-group">
                <InputProps
                  className="form-control input-sm"
                  disabled={!canEditData}
                  label={
                    <>
                      Project
                      {projectLink}
                    </>
                  }
                  placeholder="Project"
                >
                  {suggestProject}
                </InputProps>
              </div>
              <FormGroup>
                <InputProps
                  className="form-control input-sm"
                  disabled={!canEditData}
                  label={
                    <>
                      Approval List
                      {approvalListLink}
                    </>
                  }
                  placeholder="Approval List"
                >
                  {suggestApprovalList}
                </InputProps>
              </FormGroup>
              <div className="row">
                <div className="col-xs-6">
                  <FormGroup>
                    <Input
                      className="input-sm"
                      disabled={!canEditData || !unwrap(buyer.get('projectId'))}
                      label="Priority"
                      name={`${prefix}.priority`}
                      placeholder="Priority"
                      value={buyer.get('priority')}
                      showLabel
                    />
                  </FormGroup>
                </div>
                <div className={classNames(style.switcher, 'col-xs-6')}>
                  <ApprovedSwitcher approved={approved} changeApprove={onChangeApprove} />
                </div>
              </div>
              <FormGroup>
                <Textarea
                  disabled={!canEditData}
                  label="Status Summary"
                  name={`${prefix}.bsnHarvco`}
                  placeholder="Status Summary"
                  rows="2"
                  value={buyer.get('bsnHarvco')}
                  showLabel
                />
              </FormGroup>
            </div>
            <div className="col-xs-6">
              <FormGroup>
                <div className={style.tabs}>
                  <input
                    className={style.harveyTab}
                    defaultChecked={checkDefaultComment('harvey')}
                    id="tab-btn-1"
                    name="tab-btn"
                    type="radio"
                    value=""
                  />
                  <label htmlFor="tab-btn-1">
                    Harvey Comments
                  </label>
                  <input
                    className={style.clientTab}
                    defaultChecked={checkDefaultComment('client')}
                    id="tab-btn-2"
                    name="tab-btn"
                    type="radio"
                    value=""
                  />
                  <label htmlFor="tab-btn-2">
                    Client Comments
                  </label>

                  <div className={style.harveyContent}>
                    <Textarea
                      className={style.textarea}
                      disabled={!canEditData}
                      name={`${prefix}.harveyComment`}
                      placeholder="Harvey Comments"
                      rows="2"
                      showLabel={false}
                      value={buyer.get('harveyComment')}
                    />
                  </div>
                  <div className={style.clientContent}>
                    <Textarea
                      className={style.textarea}
                      disabled={!canEditData}
                      name={`${prefix}.bsnClient`}
                      placeholder="Client Comments"
                      rows="2"
                      showLabel={false}
                      value={buyer.get('bsnClient')}
                    />
                  </div>
                </div>
              </FormGroup>
            </div>
          </div>
        </div>
        <div className="text-right">
          <i className={stateClass}> {activeState} </i>
        </div>
      </div>
    </Collapse>
  );

  return (
    <div className="panel panel-default">
      {header}
      {body}
    </div>
  );
};

CompanyBuyerComponent.propTypes = {
  buyer: PropTypes.instanceOf(Map).isRequired,
  id: PropTypes.string,
  onActivate: PropTypes.func.isRequired,
  onClick: PropTypes.func,
  onDelete: PropTypes.func.isRequired,
};

/** Company buyer component. */
export const CompanyBuyer = memo(CompanyBuyerComponent);
